import { Link, useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Table,
  Button,
  Select,
  Tag,
  Space,
  Row,
  Col,
  Typography,
  Card,
  Modal,
  Input,
  InputNumber,
  Form,
  message,
  Spin,
  Upload,
  Steps,
  Alert,
  Result,
  Collapse,
} from 'antd';
import PartNumberBuilder from '../../components/PartNumberBuilder';
import MasterSheetImportModal from '../../components/MasterSheetImportModal';
import type { PartNumberBuilderValue } from '../../components/PartNumberBuilder';
import ConfigurableTable from '../../components/ConfigurableTable';
import type { ConfigurableColumn } from '../../components/ConfigurableTable';
import {
  ItemCell,
  MoneyCell,
  VendorCell,
  MissingValue,
  DensityToggle,
  useDensity,
  classOfCode,
  hasArabicInCode,
} from '../../components/table-cells';
import { PlusOutlined, UploadOutlined, DownloadOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  catalogList,
  composerSyncStatus,
  catalogCreate,
  catalogUpdate,
  catalogDelete,
  catalogBulkCreate,
  getLookups,
  catalogPricesBatch,
} from '../../api';
import type { ItemPrices } from '../../api';
import type {
  CatalogItem,
  CatalogCreatePayload,
  CatalogUpdatePayload,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import CategoryTreeSelect from '../../components/CategoryTreeSelect';
import ItemThumb from '../../components/ItemThumb';
import { useItemPictures } from '../../hooks/useItemPictures';
import { catMatches } from '../../utils/searchNorm';

const { Title, Text } = Typography;

interface Lookup {
  id: number;
  name: string;
  code?: string;
  abbreviation?: string;
}

interface CurrencyLookup {
  id: number;
  code: string;
  name?: string;
}

interface Lookups {
  vendors: Lookup[];
  categories: Lookup[];
  categoryTree: any[];
  units: Lookup[];
  currencies: CurrencyLookup[];
}

interface FormValues {
  partNumber: string;
  description: string;
  defaultUnitPrice?: number | null;
  unitId?: number | null;
  categoryId?: number | null;
  preferredVendorId?: number | null;
  currencyId?: number | null;
  sku?: string | null;
  barcode?: string | null;
  allowedUnitCategories?: string[] | null;
  priceChangeReason?: string;
}

const UNIT_CATEGORY_OPTIONS = [
  { label: 'LENGTH (meter, cm)', value: 'LENGTH' },
  { label: 'WEIGHT (grams, Kilo)', value: 'WEIGHT' },
  { label: 'COUNT (pcs, pack, roll)', value: 'COUNT' },
  { label: 'AREA', value: 'AREA' },
  { label: 'VOLUME', value: 'VOLUME' },
];

const fmtPrice = (val: number | null | undefined): string => {
  if (val === null || val === undefined) return '—';
  return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Class chips, in the order the master sheet is organised.
const CLASS_ORDER: Array<{ key: string; label: string }> = [
  { key: 'F', label: 'Fabrics' },
  { key: 'B', label: 'Beads & Sequins' },
  { key: 'A', label: 'Trims & Accessories' },
  { key: 'SRV', label: 'Services' },
  { key: 'CON', label: 'Consumables' },
  { key: 'MAT', label: 'Materials' },
  { key: 'EQP', label: 'Equipment & Tools' },
];

type GapKey = 'price' | 'vendor' | 'unit' | 'picture';
// `hasPicture` comes from the pictures map, which is loaded for the whole catalogue alongside it.
const GAP_CHIPS: Array<{ key: GapKey; label: string; test: (i: CatalogItem, hasPicture: boolean) => boolean }> = [
  { key: 'price', label: 'No price', test: (i) => i.defaultUnitPrice === null || i.defaultUnitPrice === undefined },
  { key: 'vendor', label: 'No vendor', test: (i) => !i.preferredVendor },
  { key: 'unit', label: 'No unit', test: (i) => !i.unit },
  { key: 'picture', label: 'No picture', test: (_i, hasPicture) => !hasPicture },
];

// The master list is ~800 rows and the server has no class filter, so the page
// loads the whole catalogue once and filters/pages it in the browser. That is
// also what makes the chip counts free (no extra endpoint).
const LOAD_PAGE_SIZE = 2000;

const isItemActive = (i: CatalogItem): boolean => (i.isActive ?? i.active) !== false;

const CatalogList: React.FC = () => {
  const navigate = useNavigate();
  const { isProcurement, isAdmin, canAccess } = useAuth();
  const canManage = canAccess('purchasing.catalog', 'full');
  // Requesters never see prices (same rule as the catalog browser).
  const showPrice = canManage;

  // Table state — the full list, filtered and paged client-side
  const [allItems, setAllItems] = useState<CatalogItem[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [density, setDensity] = useDensity('catalog-list');
  // Composer sync (2026-09-10): show how many pushes wait for review
  const [syncStaged, setSyncStaged] = useState(0);
  useEffect(() => { if (canManage) composerSyncStatus().then((st) => setSyncStaged(st.staged)).catch(() => {}); }, [canManage]);
  const compact = density === 'compact';

  // Filters
  const initialSearch = () => { try { const fromUrl = new URLSearchParams(window.location.search).get('search'); if (fromUrl) return fromUrl; const v = sessionStorage.getItem('catalog.search') || ''; sessionStorage.removeItem('catalog.search'); return v; } catch { return ''; } }; // handed over by ?search= or the global search
  const [search, setSearch] = useState(initialSearch); // what is typed
  const [appliedSearch, setAppliedSearch] = useState(search); // debounced, what filters
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [vendorIds, setVendorIds] = useState<number[]>([]);
  const [activeFilter, setActiveFilter] = useState<string[]>(['active']);
  const [classFilter, setClassFilter] = useState<string | null>(null); // single-select class chip
  const [gapFilters, setGapFilters] = useState<GapKey[]>([]); // multi-select gap chips
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Price engine rows for the rows currently on screen
  const [prices, setPrices] = useState<Record<number, ItemPrices>>({});
  const pricesReqRef = useRef<Set<number>>(new Set());

  // Item pictures (2026-09-22). Asked for the whole loaded catalogue, not just the page, because the
  // "No picture" chip counts and filters across the list the same way the other gap chips do.
  const allItemIds = useMemo(() => allItems.map((i) => i.id), [allItems]);
  const { pictures, reload: reloadPicture } = useItemPictures(allItemIds);

  // Lookups
  const [lookups, setLookups] = useState<Lookups>({ vendors: [], categories: [], categoryTree: [], units: [], currencies: [] });
  const [lookupsLoading, setLookupsLoading] = useState(false);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<CatalogItem | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showPriceReason, setShowPriceReason] = useState(false);
  const [form] = Form.useForm<FormValues>();
  // Inventory Phase 1.6: structured part-number builder state (lives outside the
  // Ant Form because the builder is a custom composite control)
  const EMPTY_BUILDER: PartNumberBuilderValue = {
    classCode: null,
    itemCode: null,
    colorCode: null,
    colorVariant: null,
    sizeCode: null,
    softnessCode: null,
    supplierCode: null,
    partNumber: '',
  };
  const [builderValue, setBuilderValue] = useState<PartNumberBuilderValue>(EMPTY_BUILDER);


  // CSV Import state
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [masterModalOpen, setMasterModalOpen] = useState(false);
  const [csvStep, setCsvStep] = useState(0); // 0=upload, 1=preview, 2=result
  const [csvRows, setCsvRows] = useState<Array<Record<string, string>>>([]);
  const [csvErrors, setCsvErrors] = useState<Array<{ row: number; partNumber: string; error: string }>>([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState<{ created: number; failed: number; errors: Array<{ row: number; partNumber: string; error: string }> } | null>(null);

  // ── CSV parsing helpers ───────────────────────────────────────────────────────
  const CSV_COLUMNS = ['Part Number', 'Description', 'Default Price', 'SKU', 'Barcode', 'Unit', 'Category', 'Vendor', 'Currency'];

  const parseCSV = (text: string): Array<Record<string, string>> => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    // Parse header
    const headerLine = lines[0];
    const headers = headerLine.split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
    // Parse rows
    const rows: Array<Record<string, string>> = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = parseCSVLine(lines[i]);
      if (vals.every((v) => !v.trim())) continue; // skip empty rows
      const row: Record<string, string> = {};
      headers.forEach((h, idx) => {
        row[h] = vals[idx]?.trim() || '';
      });
      rows.push(row);
    }
    return rows;
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result;
  };

  const handleCSVUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const buffer = e.target?.result as ArrayBuffer;
      const bytes = new Uint8Array(buffer);

      // Detect and skip UTF-8 BOM
      let startOffset = 0;
      if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
        startOffset = 3;
      }

      // Decode as UTF-8 first
      let text = new TextDecoder('UTF-8').decode(bytes.slice(startOffset));

      // If result contains replacement chars (Arabic garbled), retry with windows-1256
      if (text.includes('\uFFFD') || /\?{3,}/.test(text)) {
        try {
          text = new TextDecoder('windows-1256').decode(bytes.slice(startOffset));
        } catch {
          // fallback: keep UTF-8 text
        }
      }

      const rows = parseCSV(text);
      if (rows.length === 0) {
        message.error('No data rows found in CSV file');
        return;
      }
      // Validate rows client-side
      const errors: Array<{ row: number; partNumber: string; error: string }> = [];
      const seenParts = new Set<string>();
      rows.forEach((row, idx) => {
        const partNum = row['Part Number'] || '';
        if (!partNum) errors.push({ row: idx + 1, partNumber: '', error: 'Part number is required' });
        else if (seenParts.has(partNum)) errors.push({ row: idx + 1, partNumber: partNum, error: 'Duplicate part number in CSV' });
        else seenParts.add(partNum);
        if (!row['Description']) errors.push({ row: idx + 1, partNumber: partNum, error: 'Description is required' });
        if (row['Default Price'] && isNaN(Number(row['Default Price']))) errors.push({ row: idx + 1, partNumber: partNum, error: 'Invalid price value' });
      });
      setCsvRows(rows);
      setCsvErrors(errors);
      setCsvStep(1);
    };
    reader.readAsArrayBuffer(file);
    return false; // prevent upload
  };

  const handleRemoveCsvRow = (idx: number) => {
    const newRows = csvRows.filter((_, i) => i !== idx);
    setCsvRows(newRows);
    // Re-validate
    const errors: Array<{ row: number; partNumber: string; error: string }> = [];
    const seenParts = new Set<string>();
    newRows.forEach((row, i) => {
      const partNum = row['Part Number'] || '';
      if (!partNum) errors.push({ row: i + 1, partNumber: '', error: 'Part number is required' });
      else if (seenParts.has(partNum)) errors.push({ row: i + 1, partNumber: partNum, error: 'Duplicate part number in CSV' });
      else seenParts.add(partNum);
      if (!row['Description']) errors.push({ row: i + 1, partNumber: partNum, error: 'Description is required' });
      if (row['Default Price'] && isNaN(Number(row['Default Price']))) errors.push({ row: i + 1, partNumber: partNum, error: 'Invalid price value' });
    });
    setCsvErrors(errors);
  };

  const handleCsvImport = async () => {
    setCsvImporting(true);
    try {
      const items = csvRows.map((row) => ({
        partNumber: row['Part Number'] || '',
        description: row['Description'] || '',
        defaultUnitPrice: row['Default Price'] ? Number(row['Default Price']) : undefined,
        sku: row['SKU'] || undefined,
        barcode: row['Barcode'] || undefined,
        unitName: row['Unit'] || undefined,
        categoryName: row['Category'] || undefined,
        vendorName: row['Vendor'] || undefined,
        currencyCode: row['Currency'] || undefined,
      }));
      const result = await catalogBulkCreate(items);
      setCsvResult({
        created: result.created.length,
        failed: result.errors.length,
        errors: result.errors,
      });
      setCsvStep(2);
      if (result.created.length > 0) fetchItems();
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Import failed');
    } finally {
      setCsvImporting(false);
    }
  };

  const closeCsvModal = () => {
    setCsvModalOpen(false);
    setCsvStep(0);
    setCsvRows([]);
    setCsvErrors([]);
    setCsvResult(null);
  };

  const downloadTemplate = () => {
    const header = CSV_COLUMNS.join(',');
    const example = 'PART-001,"Sample fabric roll",150.00,SKU-001,,Meter,Fabrics,Local Market,EGP';
    const blob = new Blob([header + '\n' + example + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'catalog_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Load lookups once ────────────────────────────────────────────────────────
  useEffect(() => {
    setLookupsLoading(true);
    getLookups()
      .then((data) => setLookups(data as unknown as Lookups))
      .catch(() => message.error('Failed to load lookup data'))
      .finally(() => setLookupsLoading(false));
  }, []);

  // ── Fetch table data (whole catalogue, once) ─────────────────────────────────
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogList({ page: 1, pageSize: LOAD_PAGE_SIZE });
      setAllItems(res.items);
      setPrices({});
      pricesReqRef.current = new Set();
    } catch {
      message.error('Failed to load catalog items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // ── Debounced search ─────────────────────────────────────────────────────────
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setAppliedSearch(value), 300);
  };

  // ── Filter change helpers ────────────────────────────────────────────────────
  const handleCategoryChange = (vals: number[]) => setCategoryIds(vals);
  const handleVendorChange = (vals: number[]) => setVendorIds(vals);
  const handleActiveChange = (vals: string[]) => setActiveFilter(vals);

  // ── Client-side filtering, counts and paging ─────────────────────────────────
  const activeCount = useMemo(() => allItems.filter(isItemActive).length, [allItems]);
  const inactiveCount = allItems.length - activeCount;

  /** Everything the dropdowns and the search box narrow to — before the chips. */
  const baseItems = useMemo(() => {
    const q = appliedSearch.trim();
    return allItems.filter((it) => {
      if (activeFilter.length === 1) {
        if (activeFilter[0] === 'active' && !isItemActive(it)) return false;
        if (activeFilter[0] === 'inactive' && isItemActive(it)) return false;
      }
      if (categoryIds.length > 0 && !(it.category && categoryIds.indexOf(it.category.id) >= 0)) return false;
      if (vendorIds.length > 0 && !(it.preferredVendor && vendorIds.indexOf(it.preferredVendor.id) >= 0)) return false;
      // Same Arabic-safe folding as the MR "Browse catalog" search (ا/أ/إ/آ, ة/ه, ى/ي, digits, diacritics,
      // hyphens/spaces ignored), so ازم finds أزمير and "f s sat" finds F-S-SAT-…
      if (q && !catMatches(q, it.partNumber, it.description, it.descriptionAr, it.sku)) return false;
      return true;
    });
  }, [allItems, appliedSearch, activeFilter, categoryIds, vendorIds]);

  const classCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    baseItems.forEach((it) => {
      const cls = classOfCode(it.partNumber);
      if (cls) counts[cls.key] = (counts[cls.key] || 0) + 1;
    });
    return counts;
  }, [baseItems]);

  const gapCounts = useMemo(() => {
    const counts: Record<GapKey, number> = { price: 0, vendor: 0, unit: 0, picture: 0 };
    baseItems.forEach((it) => {
      const hasPicture = !!pictures[it.id];
      GAP_CHIPS.forEach((g) => {
        if (g.test(it, hasPicture)) counts[g.key] += 1;
      });
    });
    return counts;
  }, [baseItems, pictures]);

  const filteredItems = useMemo(
    () =>
      baseItems.filter((it) => {
        if (classFilter && classOfCode(it.partNumber)?.key !== classFilter) return false;
        const hasPicture = !!pictures[it.id];
        for (let i = 0; i < gapFilters.length; i++) {
          const gap = GAP_CHIPS.find((g) => g.key === gapFilters[i]);
          if (gap && !gap.test(it, hasPicture)) return false;
        }
        return true;
      }),
    [baseItems, classFilter, gapFilters, pictures]
  );

  // Any filter change sends the reader back to page 1
  useEffect(() => {
    setPage(1);
  }, [appliedSearch, activeFilter, categoryIds, vendorIds, classFilter, gapFilters]);

  const pageItems = useMemo(
    () => filteredItems.slice((page - 1) * pageSize, page * pageSize),
    [filteredItems, page, pageSize]
  );

  // ── Price engine for the visible rows ────────────────────────────────────────
  useEffect(() => {
    if (!showPrice) return;
    const ids = pageItems.map((i) => i.id).filter((id) => !pricesReqRef.current.has(id));
    if (ids.length === 0) return;
    ids.forEach((id) => pricesReqRef.current.add(id));
    catalogPricesBatch(ids)
      .then((res) => setPrices((prev) => ({ ...prev, ...res })))
      .catch(() => {
        // the table still renders; the rule and quotes columns just stay empty
        ids.forEach((id) => pricesReqRef.current.delete(id));
      });
  }, [pageItems, showPrice]);

  /** "list · Local Market · 6 Sep" / "last paid · PO-000487 · 28 Aug" */
  const priceRuleOf = (p?: ItemPrices): { text: string | null; tone: 'muted' | 'paid' } => {
    if (!p || p.current.price == null) return { text: null, tone: 'muted' };
    const rule = p.current.rule || '';
    if (/^catalog default/i.test(rule)) return { text: 'catalog default', tone: 'muted' };
    const paid = /^last paid/i.test(rule);
    const vendor = p.vendors.find((v) => v.vendorId === p.current.vendorId);
    const ref = paid ? vendor?.lastPo || p.current.vendorName : p.current.vendorName;
    const date = p.current.date ? dayjs(p.current.date).format('D MMM') : null;
    const parts = [paid ? 'last paid' : 'list', ref, date].filter(Boolean) as string[];
    return { text: parts.join(' · '), tone: paid ? 'paid' : 'muted' };
  };

  // ── Modal open/close ─────────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditItem(null);
    setShowPriceReason(false);
    form.resetFields();
    setBuilderValue(EMPTY_BUILDER);
    setModalOpen(true);
  };

  const openEditModal = (item: CatalogItem) => {
    setEditItem(item);
    setShowPriceReason(false);
    const allowedArr = item.allowedUnitCategories
      ? item.allowedUnitCategories.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
    form.setFieldsValue({
      partNumber: item.partNumber,
      description: item.description,
      defaultUnitPrice: item.defaultUnitPrice ?? undefined,
      unitId: item.unit?.id ?? null,
      categoryId: item.category?.id ?? null,
      preferredVendorId: item.preferredVendor?.id ?? null,
      currencyId: item.currency?.id ?? null,
      sku: item.sku ?? null,
      barcode: item.barcode ?? null,
      allowedUnitCategories: allowedArr,
      priceChangeReason: undefined,
    });
    // Inventory Phase 1.6: hydrate builder from persisted structured segments
    setBuilderValue({
      classCode: item.classCode ?? null,
      itemCode: item.itemCode ?? null,
      colorCode: item.colorCode ?? null,
      colorVariant: item.colorVariant ?? null,
      sizeCode: item.sizeCode ?? null,
      softnessCode: item.softnessCode ?? null,
      supplierCode: item.supplierCode ?? null,
      partNumber: item.partNumber,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditItem(null);
    setShowPriceReason(false);
    form.resetFields();
    setBuilderValue(EMPTY_BUILDER);
  };

  // ── Detect price change for reason field ─────────────────────────────────────
  const handlePriceChange = (val: number | null) => {
    if (editItem) {
      const originalPrice = editItem.defaultUnitPrice ?? null;
      const changed = val !== originalPrice;
      setShowPriceReason(changed);
      if (!changed) {
        form.setFieldValue('priceChangeReason', undefined);
      }
    }
  };

  // ── Save (create/update) ─────────────────────────────────────────────────────
  const handleSave = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const allowedUnitCategoriesStr =
      values.allowedUnitCategories && values.allowedUnitCategories.length > 0
        ? values.allowedUnitCategories.join(',')
        : null;

    setModalLoading(true);
    try {
      // Inventory Phase 1.6: structured segments captured from the builder.
      // Sent on both create and update so downstream reports/MRP can rely on them.
      const structuredSegments = {
        classCode: builderValue.classCode,
        itemCode: builderValue.itemCode,
        colorCode: builderValue.colorCode,
        colorVariant: builderValue.colorVariant,
        sizeCode: builderValue.sizeCode,
        softnessCode: builderValue.softnessCode,
        supplierCode: builderValue.supplierCode,
      };

      if (editItem) {
        const payload: CatalogUpdatePayload = {
          partNumber: values.partNumber,
          description: values.description,
          defaultUnitPrice: values.defaultUnitPrice ?? null,
          unitId: values.unitId ?? null,
          categoryId: values.categoryId ?? null,
          preferredVendorId: values.preferredVendorId ?? null,
          currencyId: values.currencyId ?? null,
          sku: values.sku ?? null,
          barcode: values.barcode ?? null,
          allowedUnitCategories: allowedUnitCategoriesStr,
          ...structuredSegments,
        };
        if (showPriceReason && values.priceChangeReason) {
          payload.priceChangeReason = values.priceChangeReason;
        }
        await catalogUpdate(editItem.id, payload);
        message.success('Item updated');
      } else {
        const payload: CatalogCreatePayload = {
          partNumber: values.partNumber,
          description: values.description,
          defaultUnitPrice: values.defaultUnitPrice ?? null,
          unitId: values.unitId ?? null,
          categoryId: values.categoryId ?? null,
          preferredVendorId: values.preferredVendorId ?? null,
          currencyId: values.currencyId ?? null,
          sku: values.sku ?? null,
          barcode: values.barcode ?? null,
          allowedUnitCategories: allowedUnitCategoriesStr,
          ...structuredSegments,
        };
        await catalogCreate(payload);
        message.success('Item created');
      }
      closeModal();
      fetchItems();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Failed to save item');
    } finally {
      setModalLoading(false);
    }
  };

  // ── Deactivate (lives in the edit dialog, not on the row) ────────────────────
  const handleDeactivate = (item: CatalogItem) => {
    Modal.confirm({
      title: `Deactivate "${item.partNumber}"?`,
      content: 'It stays in the catalogue and on past documents, but can no longer be requested.',
      okText: 'Deactivate',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await catalogDelete(item.id);
          message.success(`"${item.partNumber}" deactivated`);
          closeModal();
          fetchItems();
        } catch (err: any) {
          message.error(err?.response?.data?.message ?? 'Failed to deactivate item');
        }
      },
    });
  };

  // Row click and the code both open the Item Card (2026-09-09) — one destination for an item, like every ERP.
  const openCard = (id: number, section?: string) => navigate(`/catalog/${id}${section ? `#${section}` : ''}`);

  // ── Table columns ─────────────────────────────────────────────────────────────
  const columns: ConfigurableColumn<CatalogItem>[] = [
    {
      // Item pictures (2026-09-22): 36px so the row height does not change. The thumbnail opens the
      // full-size view; the row click still goes to the item card.
      title: 'Photo',
      key: 'photo',
      width: 52,
      render: (_: unknown, record: CatalogItem) => {
        const pic = pictures[record.id];
        return (
          <ItemThumb
            url={pic?.url}
            itemId={record.id}
            code={record.partNumber}
            description={record.description}
            descriptionAr={record.descriptionAr}
            bytes={pic?.size ?? null}
            date={pic?.updatedAt ?? null}
            onError={() => reloadPicture(record.id)}
          />
        );
      },
    },
    {
      title: 'Item',
      key: 'item',
      hideable: false,
      // ConfigurableTable uses tableLayout="fixed": a column without a width gets whatever
      // is left, which collapses to nothing once the other columns fill the container.
      width: 520,
      render: (_: unknown, record: CatalogItem) => (
        <ItemCell
          partNumber={record.partNumber}
          description={record.description}
          descriptionAr={record.descriptionAr}
          linkTo={`/catalog/${record.id}`}
          compact={compact}
          flags={
            hasArabicInCode(record.partNumber) ? (
              <Tag color="warning" style={{ fontSize: 11, lineHeight: '18px', marginInlineEnd: 0 }}>
                Arabic inside the code
              </Tag>
            ) : null
          }
        />
      ),
    },
    {
      title: 'Unit',
      key: 'unit',
      width: 90,
      render: (_: unknown, record: CatalogItem) =>
        record.unit?.name ?? <MissingValue label="no unit" compact={compact} />,
    },
    ...(showPrice
      ? ([
          {
            title: 'Price',
            key: 'price',
            width: 160,
            align: 'right' as const,
            render: (_: unknown, record: CatalogItem) => {
              const p = prices[record.id];
              const rule = priceRuleOf(p);
              const amount = p?.current.price ?? record.defaultUnitPrice;
              return (
                <MoneyCell
                  amount={amount}
                  currency={record.currency?.code}
                  rule={rule.text}
                  ruleTone={rule.tone}
                  align="right"
                  compact={compact}
                />
              );
            },
          },
        ] as ConfigurableColumn<CatalogItem>[])
      : []),
    {
      title: 'Preferred vendor',
      key: 'preferredVendor',
      width: 180,
      render: (_: unknown, record: CatalogItem) => (
        <VendorCell name={record.preferredVendor?.name} />
      ),
    },
    ...(showPrice
      ? ([
          {
            title: 'Quotes',
            key: 'quotes',
            width: 80,
            align: 'right' as const,
            render: (_: unknown, record: CatalogItem) => {
              const p = prices[record.id];
              if (!p) return null;
              const n = p.vendors.filter((v) => v.listPrice != null || v.lastPaid != null).length;
              return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</span>;
            },
          },
        ] as ConfigurableColumn<CatalogItem>[])
      : []),
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      hideable: false,
      render: (_: unknown, record: CatalogItem) => (
        <Space size={0}>
          {canManage && (
            <Button type="link" size="small" onClick={() => openEditModal(record)}>
              Edit
            </Button>
          )}
          <Button type="link" size="small" onClick={() => openCard(record.id, 'pricing')}>
            Prices
          </Button>
        </Space>
      ),
    },
    // Kept available behind Columns — off the default reading path
    {
      title: 'Category',
      key: 'category',
      width: 130,
      render: (_: unknown, record: CatalogItem) => record.category?.name ?? '—',
    },
    {
      title: 'SKU',
      key: 'sku',
      width: 110,
      render: (_: unknown, record: CatalogItem) => record.sku ?? '—',
    },
    {
      title: 'Currency',
      key: 'currency',
      width: 90,
      render: (_: unknown, record: CatalogItem) => record.currency?.code ?? '—',
    },
    {
      title: 'Active',
      key: 'active',
      width: 90,
      render: (_: unknown, record: CatalogItem) =>
        isItemActive(record) ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
  ];


  return (
    <div style={{ padding: '24px' }}>
      {/* Page header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Parts Catalog
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {activeCount} active · {inactiveCount} inactive
          </Text>
        </Col>
        <Col>
          <Space>
            <DensityToggle value={density} onChange={setDensity} />
            {canManage && (
              <>
                <Link to="/catalog/composer-sync"><Button icon={<SyncOutlined />}>Composer sync{syncStaged ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#a8690f', background: '#fff4dc', padding: '0 6px', borderRadius: 999 }}>{syncStaged} to review</span> : null}</Button></Link>
                <Button icon={<UploadOutlined />} onClick={() => setMasterModalOpen(true)}>
                  Import Master Sheet
                </Button>
                <MasterSheetImportModal
                  open={masterModalOpen}
                  onClose={(imported) => { setMasterModalOpen(false); if (imported) fetchItems(); }}
                />
                <Button icon={<UploadOutlined />} onClick={() => setCsvModalOpen(true)}>
                  Import CSV
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                  Add Item
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>

      {/* Filter bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input.Search
              placeholder="Search code, English or Arabic"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              allowClear
              size="small"
            />
          </Col>
          <Col xs={12} sm={6} md={5}>
            <CategoryTreeSelect
              multiple
              placeholder="Category"
              allowClear
              size="small"
              style={{ width: '100%' }}
              loading={lookupsLoading}
              onChange={handleCategoryChange}
              value={categoryIds}
              categoryTree={lookups.categoryTree}
              categories={lookups.categories}
            />
          </Col>
          <Col xs={12} sm={6} md={5}>
            <Select
              mode="multiple"
              placeholder="Vendor"
              allowClear
              size="small"
              style={{ width: '100%' }}
              loading={lookupsLoading}
              onChange={handleVendorChange}
              value={vendorIds}
              maxTagCount="responsive"
              optionFilterProp="children"
            >
              {lookups.vendors.map((v) => (
                <Select.Option key={v.id} value={v.id}>
                  {v.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={4} md={4}>
            <Select
              mode="multiple"
              size="small"
              style={{ width: '100%' }}
              value={activeFilter}
              onChange={handleActiveChange}
              maxTagCount="responsive"
              placeholder="Status"
            >
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Class + gap chips — counts come from the list already loaded */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <Tag.CheckableTag checked={classFilter === null} onChange={() => setClassFilter(null)}>
          All <span style={{ opacity: 0.65 }}>{baseItems.length}</span>
        </Tag.CheckableTag>
        {CLASS_ORDER.map((c) => (
          <Tag.CheckableTag
            key={c.key}
            checked={classFilter === c.key}
            onChange={(checked) => setClassFilter(checked ? c.key : null)}
          >
            {c.label} <span style={{ opacity: 0.65 }}>{c.key} · {classCounts[c.key] || 0}</span>
          </Tag.CheckableTag>
        ))}
        <span style={{ width: 1, height: 20, background: '#d9d9d9', margin: '0 4px' }} />
        {GAP_CHIPS.map((g) => {
          const on = gapFilters.indexOf(g.key) >= 0;
          return (
            <Button
              key={g.key}
              size="small"
              shape="round"
              type={on ? 'primary' : 'dashed'}
              danger={g.key === 'unit'}
              onClick={() =>
                setGapFilters((prev) => (on ? prev.filter((k) => k !== g.key) : prev.concat(g.key)))
              }
            >
              {g.label} <span style={{ opacity: 0.65 }}>{gapCounts[g.key]}</span>
            </Button>
          );
        })}
      </div>

      {/* Table */}
      <Card size="small">
        <ConfigurableTable<CatalogItem>
          tableId="catalog-list"
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={pageItems}
          loading={loading}
          onRow={(record) => ({
            style: { cursor: 'pointer' },
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              // links and buttons keep their own behaviour
              if ((e.target as HTMLElement).closest('a, button, .ant-btn')) return;
              openCard(record.id);
            },
          })}
          pagination={{
            current: page,
            pageSize,
            total: filteredItems.length,
            showSizeChanger: true,
            showTotal: (t) => `${t} items`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Add / Edit Modal */}
      <Modal
        title={editItem ? `Edit Item — ${editItem.partNumber}` : 'Add Catalog Item'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={closeModal}
        confirmLoading={modalLoading}
        width={860}
        destroyOnClose
        footer={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, textAlign: 'left' }}>
              {editItem && canManage && isItemActive(editItem) && (
                <Button danger onClick={() => handleDeactivate(editItem)}>
                  Deactivate
                </Button>
              )}
            </div>
            <Space>
              <Button onClick={closeModal}>Cancel</Button>
              <Button type="primary" loading={modalLoading} onClick={handleSave}>
                {editItem ? 'Save' : 'Create'}
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={form} layout="vertical" size="small">
          {/* Inventory Phase 1.6: structured part-number builder — optional */}
          <Collapse
            size="small"
            style={{ marginBottom: 12 }}
            items={[
              {
                key: 'pn-builder',
                label: 'Structured Part-Number Builder (optional)',
                children: (
                  <>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Compose the part number from the dictionary. The composed value is copied
                      into the Part Number field below when all 5 required segments are set.
                    </Typography.Text>
                    <PartNumberBuilder
                      value={builderValue}
                      onChange={(next) => {
                        setBuilderValue(next);
                        if (next.partNumber) {
                          form.setFieldValue('partNumber', next.partNumber);
                        }
                      }}
                    />
                  </>
                ),
              },
            ]}
          />

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label="Part Number"
                name="partNumber"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="SKU" name="sku">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label="Default Unit Price" name="defaultUnitPrice">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  onChange={handlePriceChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Currency" name="currencyId">
                <Select allowClear showSearch optionFilterProp="children" loading={lookupsLoading}>
                  {lookups.currencies.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.code}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Unit" name="unitId">
                <Select allowClear showSearch optionFilterProp="children" loading={lookupsLoading}>
                  {lookups.units.map((u) => (
                    <Select.Option key={u.id} value={u.id}>
                      {u.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Category" name="categoryId">
                <CategoryTreeSelect
                  allowClear
                  loading={lookupsLoading}
                  categoryTree={lookups.categoryTree}
                  categories={lookups.categories}
                  placeholder="Select category"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Preferred Vendor" name="preferredVendorId">
                <Select allowClear showSearch optionFilterProp="children" loading={lookupsLoading}>
                  {lookups.vendors.map((v) => (
                    <Select.Option key={v.id} value={v.id}>
                      {v.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Barcode" name="barcode">
            <Input />
          </Form.Item>

          <Form.Item
            label="Allowed Unit Categories"
            name="allowedUnitCategories"
            tooltip="Restrict which unit types can be used for this item on MRs. Leave empty to allow any unit."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="e.g. LENGTH + COUNT for fabric (meter/cm/pcs/roll)"
              options={UNIT_CATEGORY_OPTIONS}
            />
          </Form.Item>

          {showPriceReason && (
            <Form.Item
              label="Price Change Reason"
              name="priceChangeReason"
              rules={[{ required: true, message: 'Please provide a reason for the price change' }]}
            >
              <Input.TextArea rows={2} placeholder="Explain why the price changed" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* CSV Import Modal */}
      <Modal
        title="Import Catalog Items from CSV"
        open={csvModalOpen}
        onCancel={closeCsvModal}
        width={900}
        footer={
          csvStep === 0 ? [
            <Button key="template" icon={<DownloadOutlined />} onClick={downloadTemplate}>
              Download Template
            </Button>,
            <Button key="cancel" onClick={closeCsvModal}>Cancel</Button>,
          ] : csvStep === 1 ? [
            <Button key="back" onClick={() => { setCsvStep(0); setCsvRows([]); setCsvErrors([]); }}>Back</Button>,
            <Button key="cancel" onClick={closeCsvModal}>Cancel</Button>,
            <Button
              key="import"
              type="primary"
              loading={csvImporting}
              disabled={csvRows.length === 0 || csvErrors.length > 0}
              onClick={handleCsvImport}
            >
              Import {csvRows.length} Items
            </Button>,
          ] : [
            <Button key="done" type="primary" onClick={closeCsvModal}>Done</Button>,
          ]
        }
        destroyOnClose
      >
        <Steps
          current={csvStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Upload CSV' },
            { title: 'Preview & Validate' },
            { title: 'Result' },
          ]}
        />

        {csvStep === 0 && (
          <div>
            <Alert
              type="info"
              showIcon
              message="CSV Format"
              description={
                <span>
                  Required columns: <strong>Part Number</strong>, <strong>Description</strong>.<br />
                  Optional: Default Price, SKU, Barcode, Unit, Category, Vendor, Currency.<br />
                  Unit, Category, and Vendor are matched by name. Currency is matched by code (e.g., EGP, USD).
                </span>
              }
              style={{ marginBottom: 16 }}
            />
            <Upload.Dragger
              accept=".csv"
              maxCount={1}
              showUploadList={false}
              beforeUpload={handleCSVUpload}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined style={{ fontSize: 40, color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text">Click or drag a CSV file here</p>
              <p className="ant-upload-hint">Supports .csv files with comma-separated values</p>
            </Upload.Dragger>
          </div>
        )}

        {csvStep === 1 && (
          <div>
            <Row justify="space-between" style={{ marginBottom: 12 }}>
              <Col>
                <Space>
                  <Tag color="green"><CheckCircleOutlined /> {csvRows.length - csvErrors.length} valid</Tag>
                  {csvErrors.length > 0 && <Tag color="red"><CloseCircleOutlined /> {csvErrors.length} errors</Tag>}
                  <span style={{ color: '#888', fontSize: 12 }}>{csvRows.length} total rows</span>
                </Space>
              </Col>
            </Row>
            {csvErrors.length > 0 && (
              <Alert
                type="error"
                showIcon
                message="Fix errors before importing"
                description={csvErrors.map((e) => `Row ${e.row}: ${e.error}`).join(' | ')}
                style={{ marginBottom: 12 }}
              />
            )}
            <Table
              size="small"
              rowKey={(_, idx) => String(idx)}
              dataSource={csvRows}
              pagination={csvRows.length > 10 ? { pageSize: 10, size: 'small', showTotal: (t: number) => `${t} rows` } : false}
              scroll={{ x: 800 }}
              columns={[
                {
                  title: '#',
                  width: 50,
                  render: (_: any, record: any) => {
                    const globalIdx = csvRows.indexOf(record);
                    return globalIdx + 1;
                  },
                },
                {
                  title: 'Part Number',
                  dataIndex: 'Part Number',
                  width: 120,
                  render: (val: string, record: any) => {
                    const globalIdx = csvRows.indexOf(record);
                    const hasErr = csvErrors.some((e) => e.row === globalIdx + 1 && e.error.toLowerCase().includes('part'));
                    return <span style={hasErr ? { color: 'red', fontWeight: 600 } : {}}>{val || <em style={{ color: '#ccc' }}>empty</em>}</span>;
                  },
                },
                {
                  title: 'Description',
                  dataIndex: 'Description',
                  ellipsis: true,
                  render: (val: string, record: any) => {
                    const globalIdx = csvRows.indexOf(record);
                    const hasErr = csvErrors.some((e) => e.row === globalIdx + 1 && e.error.toLowerCase().includes('description'));
                    return <span style={hasErr ? { color: 'red', fontWeight: 600 } : {}}>{val || <em style={{ color: '#ccc' }}>empty</em>}</span>;
                  },
                },
                { title: 'Price', dataIndex: 'Default Price', width: 80, align: 'right' as const },
                { title: 'Unit', dataIndex: 'Unit', width: 70 },
                { title: 'Category', dataIndex: 'Category', width: 90 },
                { title: 'Vendor', dataIndex: 'Vendor', width: 100, ellipsis: true },
                { title: 'Currency', dataIndex: 'Currency', width: 65 },
                {
                  title: '',
                  width: 40,
                  render: (_: any, record: any) => {
                    const globalIdx = csvRows.indexOf(record);
                    return <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveCsvRow(globalIdx)} />;
                  },
                },
              ]}
            />
          </div>
        )}

        {csvStep === 2 && csvResult && (
          <Result
            status={csvResult.failed === 0 ? 'success' : csvResult.created > 0 ? 'warning' : 'error'}
            title={
              csvResult.failed === 0
                ? `${csvResult.created} items imported successfully`
                : csvResult.created > 0
                  ? `${csvResult.created} imported, ${csvResult.failed} failed`
                  : `Import failed — ${csvResult.failed} errors`
            }
            subTitle={csvResult.errors.length > 0 ? (
              <div style={{ textAlign: 'left', maxWidth: 500, margin: '0 auto' }}>
                <strong>Errors:</strong>
                <ul style={{ marginTop: 4 }}>
                  {csvResult.errors.map((e, i) => (
                    <li key={i} style={{ color: '#ff4d4f' }}>
                      Row {e.row} ({e.partNumber || '?'}): {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            ) : undefined}
          />
        )}
      </Modal>

    </div>
  );
};

export default CatalogList;
